"use client";
import * as React from "react";
import { useMediaQuery } from "@uidotdev/usehooks";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";
import { Separator } from "../ui/separator";
import { cx } from "class-variance-authority";

import { X } from "lucide-react";

export function ComboBoxResponsive({
  children,
  selectedOption,
  placeholder,
  onClear,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  trigger,
  contentClassName,
  disabled,
}: {
  children: React.ReactNode;
  selectedOption?: { label: string } | null;
  placeholder?: string;
  onClear?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  contentClassName?: string;
  disabled?: boolean;
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const open = controlledOpen ?? internalOpen;
  const setOpen = setControlledOpen ?? setInternalOpen;

  const triggerLabel = selectedOption?.label ?? `${placeholder}`;

  const renderTrigger = () =>
    trigger ? (
      trigger
    ) : (
      <div className="relative w-full">
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cx(
            "w-full h-11 justify-start text-left font-normal bg-muted/50 border-0 shadow-none hover:bg-muted/80 overflow-hidden",
            selectedOption?.label ? "text-foreground" : "text-muted-foreground",
            onClear && selectedOption ? "pr-10" : "",
          )}
        >
          <span className="truncate w-full">{triggerLabel}</span>
        </Button>
        {onClear && selectedOption && (
          <div
            role="button"
            tabIndex={0}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md bg-muted-foreground/20 hover:bg-muted-foreground/30 flex items-center justify-center transition-colors cursor-pointer z-10"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                onClear();
              }
            }}
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        )}
      </div>
    );

  if (isDesktop) {
    return (
      // modal so the popover owns the scroll lock and stays scrollable inside Dialog/Sheet
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger disabled={disabled} asChild>
          {renderTrigger()}
        </PopoverTrigger>
        <PopoverContent
          className={cx(
            "w-[300px] p-0 bg-card border border-border shadow-elevated",
            contentClassName,
          )}
          align="start"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {React.isValidElement(children)
            ? React.cloneElement(children as React.ReactElement<any>, {
                onOpenChange: setOpen,
              })
            : children}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>{renderTrigger()}</DrawerTrigger>
      <DrawerContent>
        <div className="mt-4 border-t">
          {React.isValidElement(children)
            ? React.cloneElement(children as React.ReactElement<any>, {
                onOpenChange: setOpen,
              })
            : children}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

type Option = {
  value: string;
  label: string;
};

// Internal component that receives onOpenChange from ComboBoxResponsive
function ComboBoxListContent({
  options,
  selected,
  setSelected,
  placeholder,
  onOpenChange,
  showClearButton = false,
  clearButtonText = "Clear selection",
  disabled,
}: {
  options: Option[];
  selected: Option | null;
  setSelected: (o: Option | null) => void;
  placeholder: string;
  onOpenChange?: (open: boolean) => void;
  showClearButton?: boolean;
  clearButtonText?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  const handleClear = () => {
    setSelected(null);
    onOpenChange?.(false);
  };

  if (disabled) return null;

  return (
    <Command>
      <CommandInput placeholder={t(placeholder)} />
      {showClearButton && selected && (
        <>
          <div className="px-2 py-2">
            <Button
              variant="outline"
              type="button"
              size="sm"
              className="w-full"
              onClick={handleClear}
            >
              {t(clearButtonText)}
            </Button>
          </div>
          <Separator />
        </>
      )}
      <CommandList>
        <CommandEmpty>{t("No results found")}</CommandEmpty>

        <CommandGroup>
          {options.map((opt) => (
            <CommandItem
              key={opt.value}
              value={`${opt.label.toLowerCase()} ${opt.value.toLowerCase()}`}
              onSelect={() => {
                setSelected(opt);
                onOpenChange?.(false);
              }}
            >
              {opt.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

export function ComboBoxList({
  options,
  selected,
  setSelected,
  placeholder = "Select option...",
  showClearButton = false,
  clearButtonText = "Clear selection",
  showInputClear = false,
  disabled,
}: {
  options: Option[];
  selected: Option | null;
  setSelected: (o: Option | null) => void;
  placeholder?: string;
  showClearButton?: boolean;
  clearButtonText?: string;
  showInputClear?: boolean;
  disabled?: boolean;
}) {
  return (
    <ComboBoxResponsive
      selectedOption={selected}
      placeholder={placeholder}
      onClear={showInputClear ? () => setSelected(null) : undefined}
      disabled={disabled}
    >
      <ComboBoxListContent
        options={options}
        selected={selected}
        setSelected={setSelected}
        placeholder={placeholder}
        showClearButton={showClearButton}
        clearButtonText={clearButtonText}
        disabled={disabled}
      />
    </ComboBoxResponsive>
  );
}
